import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle auth errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        // Non fare redirect per errori 401 sulla route di login
        const isLoginRoute = error.config?.url?.includes('/auth/login');
        
        if (error.response?.status === 401 && !isLoginRoute) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Auth
export const authApi = {
    login: (email, password) => api.post('/auth/login', { email, password }),
    register: (data) => api.post('/auth/register', data),
    getMe: () => api.get('/auth/me'),
    changePassword: (currentPassword, newPassword) => 
        api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword }),
};

// Users
export const usersApi = {
    list: () => api.get('/users'),
    listAgents: () => api.get('/users/agents'),
    create: (data) => api.post('/users', data),
    update: (userId, data) => api.put(`/users/${userId}`, data),
    toggleActive: (userId) => api.put(`/users/${userId}/toggle-active`),
    delete: (userId) => api.delete(`/users/${userId}`),
    sendSmsNotification: (agentId, caseNumber = '', activityInfo = '') => 
        api.post('/sms/send-agent-notification', { 
            agent_id: agentId, 
            case_number: caseNumber,
            activity_info: activityInfo 
        }),
};

// Clients
export const clientsApi = {
    list: (search = '') => api.get(`/clients${search ? `?search=${encodeURIComponent(search)}` : ''}`),
    get: (clientId) => api.get(`/clients/${clientId}`),
    create: (data) => api.post('/clients', data),
    update: (clientId, data) => api.put(`/clients/${clientId}`, data),
    delete: (clientId) => api.delete(`/clients/${clientId}`),
};

// Cases
export const casesApi = {
    list: () => api.get('/cases'),
    get: (caseId) => api.get(`/cases/${caseId}`),
    create: (data) => api.post('/cases', data),
    update: (caseId, data) => api.put(`/cases/${caseId}`, data),
    delete: (caseId) => api.delete(`/cases/${caseId}`),
    generateReport: (caseId) => api.get(`/cases/${caseId}/report`, { responseType: 'blob' }),
    uploadReportToDropbox: (caseId) => api.post(`/cases/${caseId}/report/dropbox`),
    // Agent assignment with dates
    assignAgent: (caseId, agentId, sendEmail = true, assignmentDates = []) => 
        api.post(`/cases/${caseId}/assign-agent`, { 
            agent_id: agentId, 
            send_email: sendEmail,
            assignment_dates: assignmentDates
        }),
    removeAgent: (caseId, agentId) => api.delete(`/cases/${caseId}/remove-agent/${agentId}`),
    // Agent schedule
    getAgentSchedule: (caseId, agentId) => api.get(`/cases/${caseId}/agent-schedule/${agentId}`),
    updateAgentSchedule: (caseId, agentId, dates) => 
        api.put(`/cases/${caseId}/agent-schedule/${agentId}`, { assignment_dates: dates }),
    // Agent hours summary
    getAgentHoursSummary: (caseId) => api.get(`/cases/${caseId}/agent-hours-summary`),
    // Investigated photo
    uploadInvestigatedPhoto: (caseId, file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post(`/cases/${caseId}/investigated-photo`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    getInvestigatedPhotoUrl: (caseId, photoId) => `${API_URL}/cases/${caseId}/investigated-photo/${photoId}`,
    deleteInvestigatedPhoto: (caseId) => api.delete(`/cases/${caseId}/investigated-photo`),
    // Additional images
    uploadAdditionalImage: (caseId, file, description = '') => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('description', description);
        return api.post(`/cases/${caseId}/additional-images`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    getAdditionalImageUrl: (caseId, imageId) => `${API_URL}/cases/${caseId}/additional-images/${imageId}`,
    deleteAdditionalImage: (caseId, imageId) => api.delete(`/cases/${caseId}/additional-images/${imageId}`),
    // Export PDF scheda indagato
    exportInvestigatedPdf: (caseId) => `${API_URL}/cases/${caseId}/investigated-pdf`,
};

// Activity Days
export const activityDaysApi = {
    list: (caseId) => api.get(`/cases/${caseId}/activity-days`),
    get: (caseId, dayId) => api.get(`/cases/${caseId}/activity-days/${dayId}`),
    create: (caseId, data) => api.post(`/cases/${caseId}/activity-days`, data),
    update: (caseId, dayId, data) => api.put(`/cases/${caseId}/activity-days/${dayId}`, data),
    delete: (caseId, dayId) => api.delete(`/cases/${caseId}/activity-days/${dayId}`),
    // Agent hours (SMS non viene più inviato qui - gestito dallo scheduler alle 5:00)
    updateAgentHours: (caseId, dayId, agentHours) => 
        api.put(`/cases/${caseId}/activity-days/${dayId}/agent-hours`, { 
            agent_hours: agentHours
        }),
};

// Events
export const eventsApi = {
    add: (caseId, dayId, data) => api.post(`/cases/${caseId}/activity-days/${dayId}/events`, data),
    update: (caseId, dayId, eventId, data) => api.put(`/cases/${caseId}/activity-days/${dayId}/events/${eventId}`, data),
    delete: (caseId, dayId, eventId) => api.delete(`/cases/${caseId}/activity-days/${dayId}/events/${eventId}`),
    // Event Media
    uploadMedia: (caseId, dayId, eventId, file, description = '') => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('description', description);
        return api.post(`/cases/${caseId}/activity-days/${dayId}/events/${eventId}/media`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    getMediaUrl: (caseId, dayId, eventId, mediaId) => `${API_URL}/cases/${caseId}/activity-days/${dayId}/events/${eventId}/media/${mediaId}`,
    deleteMedia: (caseId, dayId, eventId, mediaId) => api.delete(`/cases/${caseId}/activity-days/${dayId}/events/${eventId}/media/${mediaId}`),
};

// Media
export const mediaApi = {
    upload: (caseId, dayId, file, description = '') => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('description', description);
        return api.post(`/cases/${caseId}/activity-days/${dayId}/media`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    getUrl: (caseId, dayId, mediaId) => `${API_URL}/cases/${caseId}/activity-days/${dayId}/media/${mediaId}`,
    delete: (caseId, dayId, mediaId) => api.delete(`/cases/${caseId}/activity-days/${dayId}/media/${mediaId}`),
};

// Smart Onboarding Integration
export const onboardingApi = {
    search: (name) => api.get(`/onboarding/search`, { params: { name } }),
    getSubmission: (submissionId) => api.get(`/onboarding/submission/${submissionId}`),
    importToCase: (caseId, submissionId, { overwrite = false, includePhotos = true } = {}) =>
        api.post(`/cases/${caseId}/import-from-onboarding/${submissionId}`, null, {
            params: { overwrite, include_photos: includePhotos },
        }),
};

export default api;
